import { NgTemplateOutlet } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
} from '@angular/core';
import type { Params } from '@angular/router';
import { RouterModule } from '@angular/router';

import type { LinkTarget } from '../../core';
import { toQueryString } from '../../core';
import { isExternalUrl } from '../../pipes/link-parser/is-external.pipe';
import { parseLink } from '../../pipes/link-parser/link-parser.pipe';

// Sentinel base so relative paths can be parsed by `new URL`; stripped
// again when serializing, keeping internal hrefs relative to the domain.
const RELATIVE_URL_BASE = 'https://relative-url.invalid';

// A resolved anchor routed through the Angular router (internal,
// same-origin navigation).
interface RouterLinkModel {
	kind: 'router';
	routerLink: string[];
	queryParams?: Params;
	target?: LinkTarget;
}

interface AnchorLinkModel {
	kind: 'href';
	href: string;
	rel?: string;
	target?: LinkTarget;
}

type LinkModel = RouterLinkModel | AnchorLinkModel;

@Component({
	selector: 'valk-core-smart-link',
	imports: [RouterModule, NgTemplateOutlet],
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		<ng-template #contentTpl><ng-content /></ng-template>

		@let link = this.link();
		@if (link.kind === 'router') {
			<a
				[attr.data-test-id]="testId()"
				[class]="className()"
				[queryParams]="link.queryParams"
				[routerLink]="link.routerLink"
				[target]="link.target"
			>
				<ng-container [ngTemplateOutlet]="contentTpl" />
			</a>
		} @else {
			<a
				[attr.data-test-id]="testId()"
				[attr.href]="link.href"
				[attr.rel]="link.rel"
				[attr.target]="link.target"
				[class]="className()"
			>
				<ng-container [ngTemplateOutlet]="contentTpl" />
			</a>
		}
	`,
})
export class SmartLinkComponent {
	readonly url = input.required<string>();
	readonly queryParams = input<Params>();
	readonly className = input<string | undefined>(undefined);
	readonly testId = input<string | undefined>(undefined);
	readonly linkType = input<string | undefined>(undefined);
	readonly forceReload = input<boolean>(false);

	readonly target = input<LinkTarget | undefined>(undefined);
	readonly rel = input<string | undefined>(undefined);

	readonly link = computed<LinkModel>(() => {
		const url = this.url();

		if (this.linkType() === 'email') {
			return {
				kind: 'href',
				href: `mailto:${url}`,
				rel: undefined,
				target: undefined,
			};
		}

		if (!this.forceReload() && !isExternalUrl(url)) {
			return {
				kind: 'router',
				routerLink: [parseLink(url)],
				queryParams: this.queryParams(),
				target: this.target(),
			};
		}

		return {
			kind: 'href',
			href: this.resolvedHref(),
			rel: this.rel() ?? 'noopener noreferrer',
			target: this.target(),
		};
	});

	private readonly resolvedHref = computed<string>(() => {
		if (!this.forceReload()) {
			return this.url();
		}

		const href = new URL(parseLink(this.url()), RELATIVE_URL_BASE);
		const search = toQueryString(this.queryParams());

		for (const [key, value] of new URLSearchParams(search)) {
			href.searchParams.set(key, value);
		}

		return href.origin === RELATIVE_URL_BASE
			? href.pathname + href.search + href.hash
			: href.href;
	});
}
